import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Briefcase, Mail, RefreshCw, 
  Clock, CheckCircle2, AlertCircle, Phone, MapPin,
  GraduationCap, FileText, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import Layout from '../components/layout/Layout';
import AdminLogin, { checkAdminAuth, AdminLogoutButton } from '../components/auth/AdminLogin';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [establishments, setEstablishments] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('establishments');

  useEffect(() => {
    setIsAuthenticated(checkAdminAuth());
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [estRes, candRes, empRes, contRes] = await Promise.all([
        axios.get(`${API}/establishments`),
        axios.get(`${API}/candidates`),
        axios.get(`${API}/employers`),
        axios.get(`${API}/contacts`)
      ]);
      
      setEstablishments(estRes.data);
      setCandidates(candRes.data);
      setEmployers(empRes.data);
      setContacts(contRes.data);
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-blue-100 text-blue-700', label: 'New' },
      contacted: { color: 'bg-yellow-100 text-yellow-700', label: 'Contacted' },
      enrolled: { color: 'bg-green-100 text-green-700', label: 'Enrolled' },
      placed: { color: 'bg-emerald-100 text-emerald-700', label: 'Placed' },
      onboarded: { color: 'bg-green-100 text-green-700', label: 'Onboarded' },
      partnered: { color: 'bg-emerald-100 text-emerald-700', label: 'Partnered' },
      read: { color: 'bg-slate-100 text-slate-700', label: 'Read' },
      replied: { color: 'bg-green-100 text-green-700', label: 'Replied' }
    };
    const config = statusConfig[status] || statusConfig.new;
    return <Badge className={`${config.color} font-medium`}>{config.label}</Badge>;
  };

  const getSchemeBadge = (scheme) => {
    const schemeConfig = {
      nats: { color: 'bg-purple-100 text-purple-700', label: 'NATS' },
      naps: { color: 'bg-orange-100 text-orange-700', label: 'NAPS' },
      both: { color: 'bg-blue-100 text-blue-700', label: 'Both' }
    };
    const config = schemeConfig[scheme] || schemeConfig.nats;
    return <Badge className={`${config.color} font-medium`}>{config.label}</Badge>;
  };

  const stats = [
    { label: 'Establishments', value: establishments.length, icon: Building2, color: 'bg-blue-500' },
    { label: 'Candidates', value: candidates.length, icon: GraduationCap, color: 'bg-emerald-500' },
    { label: 'Employers', value: employers.length, icon: Briefcase, color: 'bg-purple-500' },
    { label: 'Contact Messages', value: contacts.length, icon: Mail, color: 'bg-orange-500' }
  ];

  return (
    <Layout>
      {/* Header */}
      <section className="py-8 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-slate-400 mt-1">View and manage all form submissions</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={fetchAllData} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
              <AdminLogoutButton onLogout={handleLogout} />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-6 -mt-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-slate-500 text-sm">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Tables */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="establishments" className="text-sm">
                <Building2 className="w-4 h-4 mr-2" />
                Establishments ({establishments.length})
              </TabsTrigger>
              <TabsTrigger value="candidates" className="text-sm">
                <GraduationCap className="w-4 h-4 mr-2" />
                Candidates ({candidates.length})
              </TabsTrigger>
              <TabsTrigger value="employers" className="text-sm">
                <Briefcase className="w-4 h-4 mr-2" />
                Employers ({employers.length})
              </TabsTrigger>
              <TabsTrigger value="contacts" className="text-sm">
                <Mail className="w-4 h-4 mr-2" />
                Contacts ({contacts.length})
              </TabsTrigger>
            </TabsList>

            {/* Establishments Table */}
            <TabsContent value="establishments">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-900">Establishment Registrations</h3>
                  <p className="text-sm text-slate-500">Companies registered for apprenticeship programs</p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Contact Person</TableHead>
                        <TableHead>Email / Phone</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Workforce</TableHead>
                        <TableHead>Scheme</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {establishments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                            No establishment registrations yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        establishments.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.company_name}</TableCell>
                            <TableCell>{item.contact_person}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{item.email}</div>
                                <div className="text-slate-500">{item.phone}</div>
                              </div>
                            </TableCell>
                            <TableCell>{item.location}</TableCell>
                            <TableCell>{item.workforce_size}</TableCell>
                            <TableCell>{getSchemeBadge(item.scheme)}</TableCell>
                            <TableCell>{getStatusBadge(item.status)}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(item.created_at)}
                            </TableCell>
                            <TableCell>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>{item.company_name}</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <p className="text-sm text-slate-500">Contact Person</p>
                                        <p className="font-medium">{item.contact_person}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Workforce Size</p>
                                        <p className="font-medium">{item.workforce_size}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Email</p>
                                        <p className="font-medium">{item.email}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Phone</p>
                                        <p className="font-medium">{item.phone}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Location</p>
                                        <p className="font-medium">{item.location}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Scheme</p>
                                        <p className="font-medium">{item.scheme?.toUpperCase()}</p>
                                      </div>
                                    </div>
                                    {item.message && (
                                      <div>
                                        <p className="text-sm text-slate-500">Message</p>
                                        <p className="font-medium bg-slate-50 p-3 rounded-lg">{item.message}</p>
                                      </div>
                                    )}
                                    <div className="text-sm text-slate-500">
                                      Submitted on {formatDate(item.created_at)}
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Candidates Table */}
            <TabsContent value="candidates">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-900">Candidate Registrations</h3>
                  <p className="text-sm text-slate-500">Candidates registered for skill training & placement</p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Age</TableHead>
                        <TableHead>Qualification</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Mobile / Email</TableHead>
                        <TableHead>Sector</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {candidates.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                            No candidate registrations yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        candidates.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>{item.age}</TableCell>
                            <TableCell className="capitalize">{item.qualification}</TableCell>
                            <TableCell>{item.location}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{item.mobile}</div>
                                <div className="text-slate-500">{item.email || '-'}</div>
                              </div>
                            </TableCell>
                            <TableCell className="capitalize">{item.sector}</TableCell>
                            <TableCell>{getStatusBadge(item.status)}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(item.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Employers Table */}
            <TabsContent value="employers">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-900">Employer Registrations</h3>
                  <p className="text-sm text-slate-500">Employers seeking skilled workforce</p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Contact Person</TableHead>
                        <TableHead>Email / Phone</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Requirements</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No employer registrations yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        employers.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.company_name}</TableCell>
                            <TableCell>{item.contact_person}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{item.email}</div>
                                <div className="text-slate-500">{item.phone}</div>
                              </div>
                            </TableCell>
                            <TableCell>{item.location}</TableCell>
                            <TableCell className="max-w-xs truncate">{item.requirements}</TableCell>
                            <TableCell>{getStatusBadge(item.status)}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(item.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Contacts Table */}
            <TabsContent value="contacts">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-900">Contact Messages</h3>
                  <p className="text-sm text-slate-500">Messages from the contact form</p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email / Phone</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No contact messages yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        contacts.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{item.email}</div>
                                <div className="text-slate-500">{item.phone}</div>
                              </div>
                            </TableCell>
                            <TableCell className="capitalize">{item.category}</TableCell>
                            <TableCell className="max-w-xs truncate">{item.message}</TableCell>
                            <TableCell>{getStatusBadge(item.status)}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(item.created_at)}
                            </TableCell>
                            <TableCell>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Contact Message</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <p className="text-sm text-slate-500">Name</p>
                                        <p className="font-medium">{item.name}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Category</p>
                                        <p className="font-medium capitalize">{item.category}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Email</p>
                                        <p className="font-medium">{item.email}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-slate-500">Phone</p>
                                        <p className="font-medium">{item.phone}</p>
                                      </div>
                                    </div>
                                    <div>
                                      <p className="text-sm text-slate-500">Message</p>
                                      <p className="font-medium bg-slate-50 p-3 rounded-lg">{item.message}</p>
                                    </div>
                                    <div className="text-sm text-slate-500">
                                      Submitted on {formatDate(item.created_at)}
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </Layout>
  );
};

export default AdminDashboard;
